import { LOW_STOCK_THRESHOLD } from '@/constants/site'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']
const MONTHS_LONG = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember']

/* Deterministic so server and browser render the exact same text */
export const formatNumber = (value: number): string => {
    const rounded = Math.round(Number.isFinite(value) ? value : 0)
    const sign = rounded < 0 ? '-' : ''

    return sign + Math.abs(rounded).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

export const formatRupiah = (value: number): string => {
    const rounded = Math.round(Number.isFinite(value) ? value : 0)

    return rounded < 0 ? `-Rp${formatNumber(Math.abs(rounded))}` : `Rp${formatNumber(rounded)}`
}

export const applyDiscount = (price: number, percent: number): number => {
    const safePercent = Math.min(90, Math.max(0, Math.round(percent || 0)))

    if (safePercent === 0) return price

    return Math.round((price * (100 - safePercent)) / 100 / 100) * 100
}

/* WIB (UTC+7), formatted by hand so it never depends on the visitor's timezone */
const toWib = (iso: string): Date | null => {
    const time = Date.parse(iso)

    if (Number.isNaN(time)) return null

    return new Date(time + 7 * 60 * 60 * 1000)
}

const pad = (value: number): string => value.toString().padStart(2, '0')

export const formatDate = (iso: string): string => {
    const date = toWib(iso)

    if (!date) return '-'

    return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

export const formatDateTime = (iso: string): string => {
    const date = toWib(iso)

    if (!date) return '-'

    return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}, ${pad(date.getUTCHours())}.${pad(date.getUTCMinutes())} WIB`
}

export const formatLongDate = (iso: string): string => {
    const date = toWib(iso)

    if (!date) return '-'

    return `${date.getUTCDate()} ${MONTHS_LONG[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

export const wibDayKey = (iso: string): string => {
    const date = toWib(iso)

    if (!date) return ''

    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

export const formatShortDay = (dayKey: string): string => {
    const [, month, day] = dayKey.split('-').map(Number)

    if (!month || !day) return dayKey

    return `${day} ${MONTHS[month - 1]}`
}

export const slugify = (text: string): string => {
    return text
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80)
}

/* 08xx / +62xx / 62xx all become 62xx */
export const normalizeWhatsapp = (raw: string | null | undefined): string => {
    const digits = (raw || '').replace(/\D/g, '')

    if (!digits) return ''

    if (digits.startsWith('0')) return `62${digits.slice(1)}`

    if (digits.startsWith('8')) return `62${digits}`

    return digits
}

export const whatsappLink = (number: string | null | undefined, text?: string): string | null => {
    const normalized = normalizeWhatsapp(number)

    if (normalized.length < 9) return null

    return text ? `https://wa.me/${normalized}?text=${encodeURIComponent(text)}` : `https://wa.me/${normalized}`
}

export const displayWhatsapp = (raw: string | null | undefined): string => {
    const normalized = normalizeWhatsapp(raw)

    if (!normalized) return ''

    const local = normalized.startsWith('62') ? `0${normalized.slice(2)}` : normalized

    return local.replace(/(\d{4})(\d{4})(\d+)/, '$1-$2-$3')
}

export type StockTone = 'available' | 'low' | 'out'

export const stockStatus = (stock: number): { label: string, tone: StockTone } => {
    if (stock <= 0) return { label: 'Habis', tone: 'out' }

    if (stock <= LOW_STOCK_THRESHOLD) return { label: 'Stok terbatas', tone: 'low' }

    return { label: 'Tersedia', tone: 'available' }
}

export const socialUrl = (platform: 'instagram' | 'tiktok' | 'facebook', value: string | null | undefined): string | null => {
    const raw = (value || '').trim()

    if (!raw) return null

    if (/^https?:\/\//i.test(raw)) return raw

    const handle = raw.replace(/^@/, '')

    if (platform === 'instagram') return `https://instagram.com/${handle}`

    if (platform === 'tiktok') return `https://www.tiktok.com/@${handle}`

    return `https://facebook.com/${handle}`
}

export const parseJsonArray = (value: unknown): Array<string> => {
    if (Array.isArray(value)) return value.filter((item): item is string => typeof item === 'string')

    if (typeof value !== 'string' || !value.trim()) return []

    try {
        const parsed = JSON.parse(value)

        return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []
    }
    catch {
        return []
    }
}

export const pluralItems = (count: number): string => `${count} barang`
